import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthRightSection from '../components/AuthRightSection';
import MultiSelectSpecialty from '../components/MultiSelectSpecialty';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import { signInWithGoogle, signInWithFacebook } from '../services/firebase';

// Import local assets
import FacebookIcon from '../components/FacebookIcon';

const SignUp: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const wizardData = location.state?.wizardData;

  const [formData, setFormData] = useState({
    name: wizardData?.name || '',
    phone: wizardData?.phone || '',
    email: wizardData?.email || '',
    password: '',
    confirmPassword: '',
    specialties: wizardData?.specialties || (wizardData?.specialty ? [wizardData.specialty] : []),
  });

  const handleCreateAccount = async () => {
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.phone || !formData.password) {
        throw new Error('Please fill in all required fields');
      }

      // Validate password match
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Validate password strength
      if (formData.password.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      // Prepare registration data
      const registrationData = {
        email: formData.email,
        username: formData.email, // Using email as username for simplicity
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        specialties: formData.specialties,
      };

      // Call the registration API
      const response = await authApi.register(registrationData);

      // Automatically log in the user with the access token from registration
      await login(response.access_token);

      // Navigate directly to dashboard
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      // Provide more specific error messages if available
      let errorMessage = 'Failed to create account. Please try again.';
      if (err?.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail[0]?.msg || errorMessage;
        }
      } else if ((err as any)?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setSocialLoading('google');
    setError('');

    try {
      // Sign in with Google using Firebase
      const { token } = await signInWithGoogle();

      // Send the token to our backend for authentication
      const response = await authApi.googleLogin(token);

      // Store the token and update auth context
      await login(response.access_token);

      // For social logins, navigate directly to dashboard
      // The authentication is already complete at this point
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-up was cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups for this site.');
      } else {
        // Handle validation errors properly
        let errorMessage = 'Google sign-up failed';
        if (err.response?.data?.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail[0]?.msg || 'Validation error';
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        setError(errorMessage);
      }
    } finally {
      setSocialLoading(null);
    }
  };

  const handleFacebookSignUp = async () => {
    setSocialLoading('facebook');
    setError('');

    try {
      // Sign in with Facebook using Firebase
      const { token } = await signInWithFacebook();

      // Send the token to our backend for authentication
      const response = await authApi.facebookLogin(token);

      // Store the token and update auth context
      await login(response.access_token);

      // For social logins, navigate directly to dashboard
      // The authentication is already complete at this point
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-up was cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups for this site.');
      } else {
        // Handle validation errors properly
        let errorMessage = 'Facebook sign-up failed';
        if (err.response?.data?.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail[0]?.msg || 'Validation error';
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        setError(errorMessage);
      }
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <>
      <div
        className="bg-[#ffffff] relative w-full min-h-screen flex flex-col lg:flex-row"
        data-name="1.1 Sign up - step 4"
        id="node-12_7269"
      >
        {/* Left side - Form */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div
            className="flex flex-col gap-8 sm:gap-12 lg:gap-20 items-center justify-center max-w-[673px] w-full"
            id="node-12_7286"
          >
            <div
              className="flex flex-col gap-8 items-start justify-center w-full"
              id="node-12_7287"
            >
              <div className="flex flex-col gap-0.5 items-start justify-center" id="node-12_7288">
                <div
                  className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] text-center uppercase"
                  id="node-12_7289"
                >
                  Create an account
                </div>
                <div className="flex flex-row gap-0.5 items-start justify-center" id="node-12_7290">
                  <div className="p-[2px]" data-name="Have an account login" id="node-12_7291">
                    <div
                      className="font-['Ovo',_serif] font-normal text-[#000000] text-[16px]"
                      id="node-I12_7291-2_3"
                    >
                      Already have an account?
                    </div>
                  </div>
                  <Link
                    to="/signin"
                    className="font-['Ovo',_serif] font-semibold text-[#c6a664] text-[16px] underline"
                    id="node-12_7292"
                  >
                    Log in
                  </Link>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {/* Form fields */}
              <div className="flex flex-col gap-1 w-full" data-name="Text field" id="node-12_7293">
                <label
                  className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]"
                  id="node-I12_7293-2_17"
                >
                  Enter your full name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., John Doe"
                  className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
                  id="node-I12_7293-2_18"
                />
              </div>

              <div className="flex flex-col gap-1 w-full" id="node-12_7294">
                <label
                  className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]"
                  id="node-12_7297"
                >
                  Enter your phone number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="e.g., +433 3456 5684"
                  className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
                  id="node-12_7301"
                />
              </div>

              <div className="flex flex-col gap-1 w-full" id="node-12_7310">
                <label
                  className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]"
                  id="node-12_7313"
                >
                  Enter your e-mail address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="e.g emailaddress@dot.com"
                  className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
                  id="node-12_7317"
                />
              </div>

              <div className="flex flex-col gap-1 w-full">
                <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]">
                  Enter your password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder="Minimum 8 characters"
                  className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
                />
              </div>

              <div className="flex flex-col gap-1 w-full">
                <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]">
                  Confirm your password
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))
                  }
                  placeholder="Re-enter your password"
                  className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
                />
              </div>

              <MultiSelectSpecialty
                selectedSpecialties={formData.specialties}
                onSelectionChange={(specialties) =>
                  setFormData((prev) => ({ ...prev, specialties }))
                }
                placeholder="Search and select your specialties..."
                label="Specialties"
              />

              <button
                className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[20px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                id="node-12_7342"
                onClick={handleCreateAccount}
                disabled={loading || socialLoading !== null}
              >
                {loading ? 'Creating Account...' : 'Create FREE Account'}
              </button>
            </div>

            <div
              className="flex flex-col gap-6 items-center justify-start w-full"
              id="node-12_7343"
            >
              <div
                className="flex flex-row gap-[23px] items-center justify-center w-full"
                data-name="Divider"
                id="node-12_7344"
              >
                <div
                  className="bg-[#eaeaea] h-0.5 flex-1"
                  data-name="Divider"
                  id="node-I12_7344-6_1758"
                />
                <div
                  className="font-['Ovo',_serif] text-[#c6a664] text-[24px] uppercase"
                  id="node-I12_7344-6_1759"
                >
                  OR
                </div>
                <div
                  className="bg-[#eaeaea] h-0.5 flex-1"
                  data-name="Divider"
                  id="node-I12_7344-6_1760"
                />
              </div>
              <div
                className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-start justify-center w-full"
                id="node-12_7345"
              >
                <button
                  onClick={handleFacebookSignUp}
                  disabled={loading || socialLoading !== null}
                  className="bg-[#ffffff] h-[60px] sm:h-[72px] relative rounded-lg flex-1 sm:max-w-[329px] border border-[#eaeaea] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-name="Social media signup/login"
                  id="node-12_7346"
                >
                  <div className="flex flex-row gap-2 items-center justify-center h-full">
                    <div className="relative size-8 flex items-center justify-center">
                      <FacebookIcon />
                    </div>
                    <div className="font-['Ovo',_serif] text-[#1f1f1f] text-[16px] sm:text-[18px] lg:text-[20px] uppercase">
                      {socialLoading === 'facebook' ? 'Signing up...' : 'Sign up with Facebook'}
                    </div>
                  </div>
                </button>
                <button
                  onClick={handleGoogleSignUp}
                  disabled={loading || socialLoading !== null}
                  className="bg-[#ffffff] h-[60px] sm:h-[72px] relative rounded-lg flex-1 sm:max-w-[329px] border border-[#eaeaea] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-name="Social media signup/login"
                  id="node-12_7347"
                >
                  <div className="flex flex-row gap-2 items-center justify-center h-full">
                    <div className="relative size-6 flex items-center justify-center">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                    </div>
                    <div className="font-['Ovo',_serif] text-[#1f1f1f] text-[16px] sm:text-[18px] lg:text-[20px] uppercase">
                      {socialLoading === 'google' ? 'Signing up...' : 'Sign up with Google'}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile-only benefits section */}
          <div className="block md:hidden w-full bg-[#f8f6f2] p-6 rounded-lg">
            <div className="text-center mb-4">
              <div className="font-['Ovo',_serif] text-[#000000] text-[20px] uppercase">
                Get ready to tout your CLOUT!
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Discover your initial CLOUT score
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Complete your profile to become CLOUT certified
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Invite peers to endorse you and build your CLOUT!
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Illustration */}
        <div className="hidden md:block flex-1 min-h-[400px] lg:min-h-screen">
          <AuthRightSection />
        </div>
      </div>
    </>
  );
};

export default SignUp;
